import { useEffect, useState } from "react";

import { useLanguage } from "../../i18n/language-provider";
import { fetchPricedRoomsCount } from "../../services/publicStatsApi";

interface PricedRoomsSectionProps {
    /** The section's anchor. */
    id: string;
}

/**
 * How many rooms Optivo prices: the one thing the page says about who uses
 * it.
 *
 * The page used to name the properties Optivo prices, a partner and the
 * property management systems it connects to. The owners took all three off
 * (8 Oct 2026: a competitor reads this page too) and asked for a count from
 * the database in their place. So the figure is the server's
 * (`GET /api/public/priced-rooms`), never a number typed into the copy, and
 * the section is drawn only once it has one: no placeholder, no zero, and
 * nothing at all where the count cannot be read.
 *
 * The figure stands inside the heading, so the heading alone says it, and the
 * words beside it are a label that reads the same after any number.
 */
export function PricedRoomsSection({ id }: PricedRoomsSectionProps) {
    const { t, locale } = useLanguage();
    const w = t.welcome;
    const [count, setCount] = useState<number | null>(null);

    useEffect(() => {
        let asked = true;
        void fetchPricedRoomsCount().then((answer) => {
            if (asked && answer !== null) setCount(answer);
        });
        return () => {
            asked = false;
        };
    }, []);

    if (count === null) return null;

    return (
        <section id={id} className="scroll-mt-20 border-t">
            <div className="mx-auto max-w-6xl px-6 py-16 lg:py-20">
                <p className="type-eyebrow">{w.pricedRoomsEyebrow}</p>
                <h2 className="mt-4 flex flex-col gap-x-8 gap-y-3 sm:flex-row sm:items-baseline">
                    <span
                        data-testid="priced-rooms-count"
                        className="type-display text-6xl text-emphasis lg:text-7xl"
                    >
                        {new Intl.NumberFormat(locale).format(count)}
                    </span>{" "}
                    <span className="type-display-section max-w-3xl text-3xl text-balance">
                        {w.pricedRoomsTitle}
                    </span>
                </h2>
                <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">{w.pricedRoomsDescription}</p>
            </div>
        </section>
    );
}
